import { Navigate, Route, Routes } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { BillingStatus } from '@simpleacs/shared';
import { AppShell } from './components/layout/AppShell';
import { Spinner } from './components/ui';
import { api } from './lib/api';
import { useSession } from './lib/session';
import { BillingPage } from './pages/Billing';
import { InvoiceDetailPage } from './pages/billing/InvoiceDetail';
import { PaymentDetailPage } from './pages/billing/PaymentDetail';
import { AuditPage } from './pages/Audit';
import { DashboardPage } from './pages/Dashboard';
import { DevicesPage } from './pages/Devices';
import { AuthorizeOnu } from './pages/AuthorizeOnu';
import { OnuDetailPage } from './pages/OnuDetail';
import { GatewayDetailPage } from './pages/GatewayDetail';
import { FirmwarePage } from './pages/Firmware';
import { GatewaysPage } from './pages/Gateways';
import { OltDetailPage } from './pages/OltDetail';
import { OltOnboardingPage } from './pages/OltOnboarding';
import { OltsPage } from './pages/Olts';
import { OnuTypesPage } from './pages/OnuTypes';
import { SpeedProfilesPage } from './pages/SpeedProfiles';
import { BulkActionsPage } from './pages/BulkActions';
import { PresetsPage } from './pages/Presets';
import { LoginPage } from './pages/Login';
import { RestorePage } from './pages/Restore';
import { OrganizationsPage } from './pages/Organizations';
import { OrganizationDetailPage } from './pages/OrganizationDetail';
import { OrgMigrationPage } from './pages/OrgMigration';
import { PlaceholderPage } from './pages/Placeholder';
import { ReportsPage } from './pages/Reports';
import { DriftPage } from './pages/Drift';
import { RolesPage } from './pages/Roles';
import { TopologyPage } from './pages/Topology';
import { SplittersPage } from './pages/Splitters';
import { OdfPage } from './pages/Odf';
import { SettingsPage } from './pages/Settings';
import { Tr069ProfilesPage } from './pages/Tr069Profiles';
import { GenieacsPage } from './pages/infrastructure/GenieacsPage';
import { PlatformIndexPage } from './pages/infrastructure/PlatformIndex';
import { TunnelsPage } from './pages/infrastructure/TunnelsPage';
import { DeployPage } from './pages/infrastructure/DeployPage';
import { BackupsPage } from './pages/infrastructure/BackupsPage';
import { ApiKeysPage } from './pages/ApiKeys';
import { ProfilePage } from './pages/Profile';
import { ChangelogPage } from './pages/Changelog';
import { UsersPage } from './pages/Users';
import { FieldJobsPage } from './pages/FieldJobs';

export function App() {
  const { session, isLoading } = useSession();
  const isSuperadmin = session?.user.role === 'superadmin';

  /**
   * The billing wall, the operator's rule: when a bill is due, the org's
   * people go to the billing page and nowhere else — while the OLTs, ONUs
   * and sweeps keep running untouched. The platform owner is exempt (they
   * are the till, not the debtor).
   */
  const billingQuery = useQuery({
    queryKey: ['billing', 'status'],
    queryFn: () => api.get<BillingStatus>('/billing/status'),
    enabled: Boolean(session) && !isSuperadmin,
    refetchInterval: 60_000,
    retry: false,
  });
  const walled = !isSuperadmin && billingQuery.data?.blocked === true;

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-canvas">
        <Spinner className="size-6" />
      </div>
    );
  }

  /**
   * Restoring onto a clean install is reachable WITHOUT a session, and has to
   * be: the server this page exists for has no accounts yet, so a sign-in wall
   * would make the feature impossible on exactly the machine that needs it.
   * The API gates it on a token only root on that box can read and refuses
   * outright on any install that already holds data — this route is a door,
   * not the lock. Checked before the session gate so a lost password cannot
   * strand a recovery either.
   */
  if (window.location.pathname === '/restore') return <RestorePage />;

  if (!session) return <LoginPage />;

  if (walled) {
    return (
      <Routes>
        <Route element={<AppShell />}>
          <Route path="/billing" element={<BillingPage />} />
          {/* The documents are reachable THROUGH the wall on purpose: an org
              that has been walled for an unpaid bill is precisely the one that
              needs to open, print and download the invoice it is being asked
              to pay. Sending them back to /billing would be the platform
              demanding money and hiding the demand. */}
          <Route path="/billing/invoices/:ref" element={<InvoiceDetailPage />} />
          <Route path="/billing/payments/:ref" element={<PaymentDetailPage />} />
          <Route path="*" element={<Navigate to="/billing" replace />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<DashboardPage />} />
        <Route path="/onus" element={<DevicesPage />} />
        {/* OLT-side ONU (most of the fleet never speaks TR-069). */}
        <Route path="/onus/olt/:id" element={<OnuDetailPage />} />
        <Route path="/onus/:id" element={<OnuDetailPage />} />
        <Route path="/onu-types" element={<OnuTypesPage />} />
        <Route path="/gateways" element={<GatewaysPage />} />
        <Route path="/topology" element={<TopologyPage />} />
        <Route path="/splitters" element={<SplittersPage />} />
        <Route path="/odf" element={<OdfPage />} />
        <Route path="/firmware" element={<FirmwarePage />} />
        {/* Its own page rather than a mode of the ONU list: a bulk write is a
            different act from browsing, and it needs its own plan and history. */}
        <Route path="/bulk-actions" element={<BulkActionsPage />} />
        {/* The office half of field deployments: raise, assign, close, and set
            the GPS policy. The mobile app is the field half. */}
        <Route path="/field" element={<FieldJobsPage />} />
        <Route path="/gateways/:id" element={<GatewayDetailPage />} />
        <Route path="/olts" element={<OltsPage />} />
        <Route path="/drift" element={<DriftPage />} />
        <Route path="/olts/new" element={<OltOnboardingPage />} />
        <Route path="/olts/:id" element={<OltDetailPage />} />
        {/* Its own page, not a modal: authorising an ONU is fifteen fields
            across model, service, management and placement — a stepped wizard
            like org migration, which a dialog cannot hold without hiding half. */}
        <Route path="/olts/:oltId/authorize/:serial" element={<AuthorizeOnu />} />
        <Route path="/organizations" element={<OrganizationsPage />} />
        <Route path="/organizations/:id" element={<OrganizationDetailPage />} />
        {/* Its own page rather than a dialog on the detail view: handing one
            tenant's fleet to another is a four-step decision with a review in
            the middle, and a modal cannot hold that without hiding half of it. */}
        <Route path="/organizations/:id/migration" element={<OrgMigrationPage />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/audit" element={<AuditPage />} />
        <Route path="/olt-log" element={<ReportsPage />} />
        {/* Renamed from Reports; keep the old path working. */}
        <Route path="/reports" element={<Navigate to="/olt-log" replace />} />
        <Route path="/roles" element={<RolesPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/billing/invoices/:ref" element={<InvoiceDetailPage />} />
        <Route path="/billing/payments/:ref" element={<PaymentDetailPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/tr069-profiles" element={<Tr069ProfilesPage />} />
        {/* The IA rework's home for the platform's own machinery. */}
        <Route path="/infrastructure" element={<PlatformIndexPage />} />
        <Route path="/infrastructure/genieacs" element={<GenieacsPage />} />
        <Route path="/infrastructure/tunnels" element={<TunnelsPage />} />
        <Route path="/infrastructure/deploy" element={<DeployPage />} />
        <Route path="/infrastructure/backups" element={<BackupsPage />} />
        {/* GenieACS moved to Infrastructure; old bookmarks keep landing right. */}
        <Route path="/settings/genieacs" element={<Navigate to="/infrastructure/genieacs" replace />} />
        <Route path="/settings/api-keys" element={<ApiKeysPage />} />
        <Route path="/settings/speed-profiles" element={<SpeedProfilesPage />} />
        {/* Both kinds of preset became tabs on one page. The old paths redirect
            rather than 404: they were in the nav, so they are in histories and
            bookmarks. */}
        <Route path="/settings/presets" element={<PresetsPage />} />
        <Route path="/settings/device-presets" element={<Navigate to="/settings/presets" replace />} />
        <Route path="/settings/authorize-presets" element={<Navigate to="/settings/presets" replace />} />
        {/* SmartOLT import became a TAB on Settings (operator, 2026-08-25). The
            old path redirects rather than 404s: it was in the nav, so it is in
            somebody's history and possibly a bookmark. */}
        <Route path="/settings/smartolt" element={<Navigate to="/settings" replace />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/changelog" element={<ChangelogPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
